package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.epi.FichaEpiDados
import br.com.htms.inspeccaocampo.data.funcionarios.Cpf
import br.com.htms.inspeccaocampo.platform.PlatformServices
import kotlin.math.min

/**
 * Ficha de controle e entrega de EPI no layout do modelo "FICHA DE EPI - MODELO MONTADOR":
 * uma única página A4 paisagem; todos os itens com a mesma data de retirada.
 */
object FichaEpiPdfBuilder {
    private const val W = 842.0
    private const val H = 595.0
    private const val X0 = 36.0
    private const val X1 = W - 36.0
    private const val BOTTOM = 30.0
    private const val PAD = 4.0
    private const val LOGO_ASSET = "os_logo_htms.jpg"

    /** Linhas mínimas da tabela (como no modelo) e máximo que ainda cabe na página. */
    const val LINHAS_MINIMAS = 14
    const val MAX_ITENS = 24

    private val CABECALHO = listOf(
        "RETIRADA", "QUANT", "C.A", "DESCRIÇÃO DOS EPI's", "ASSINATURA", "ENTREGUE POR", "DATA DEVOLUÇÃO", "RECEBIDO POR"
    )

    /** Larguras relativas das colunas do modelo (twips). */
    private val COLUNAS = doubleArrayOf(1271.0, 965.0, 1020.0, 3969.0, 2693.0, 1843.0, 1559.0, 2126.0)

    private fun declaracao(empresa: String) =
        "Recebi da Empresa $empresa, para meu uso obrigatório os EPI's (Equipamentos de Proteção Individual) " +
            "constantes nesta ficha, os quais obrigo-me a utilizá-los corretamente durante o tempo que permanecerem " +
            "ao meu dispor, observando as medidas gerais de disciplina e uso que integram a NR-06 - Equipamento de " +
            "Proteção Individual - EPI's - da Portaria n.º 3.214 de 08/jun/1978. Declaro saber também que terei que " +
            "devolvê-los no ato de meu desligamento da empresa."

    suspend fun build(d: FichaEpiDados): ByteArray {
        require(d.linhas.size <= MAX_ITENS) { "Máximo de $MAX_ITENS EPIs para caber em uma página." }
        val logo = PlatformServices.loadBundledAsset(LOGO_ASSET)
            ?.takeIf { it.size > 2 && it[0] == 0xFF.toByte() && it[1] == 0xD8.toByte() }
        val pg = PdfPagina(W, H, logo)
        var y = H - 36.0

        // Cabeçalho: logo | título
        val hCab = 56.0
        val xTitulo = X0 + (X1 - X0) * 2676.0 / 15446.0
        pg.rect(X0, y - hCab, X1 - X0, hCab)
        pg.vline(xTitulo, y - hCab, y)
        pg.logoSize?.let { s ->
            val lh = 36.0
            val lw = min(lh * s.width / s.height.toDouble(), xTitulo - X0 - 12)
            val lhAjust = lw * s.height / s.width.toDouble()
            pg.logo(X0 + (xTitulo - X0 - lw) / 2, y - hCab + (hCab - lhAjust) / 2, lw, lhAjust)
        }
        pg.textCentered(
            "FICHA DE CONTROLE E ENTREGA DE EQUIPAMENTO DE PROTEÇÃO INDIVIDUAL",
            xTitulo, X1, y - hCab / 2 - 4.5, 13.0, true
        )
        y -= hCab + 10

        // Identificação: 2 linhas x 3 colunas
        val totalId = 5524.0 + 4110.0 + 5812.0
        val xa = X0 + (X1 - X0) * 5524.0 / totalId
        val xb = xa + (X1 - X0) * 4110.0 / totalId
        val hId = 20.0
        fun celula(x0: Double, x1: Double, bottom: Double, rotulo: String, valor: String) {
            val fs = 9.0
            val rw = PdfTexto.largura("$rotulo ", fs, true)
            val maxW = x1 - x0 - rw - PAD * 2
            var fv = fs
            while (fv > 6.5 && PdfTexto.largura(valor, fv, false) > maxW) fv -= 0.25
            pg.text(rotulo, x0 + PAD, bottom + 6.5, fs, true)
            pg.text(PdfTexto.truncar(valor, maxW, fv, false), x0 + PAD + rw, bottom + 6.5, fv, false)
        }
        listOf(
            Triple("NOME:" to d.nome.uppercase(), "CPF:" to (if (Cpf.valido(d.cpf)) Cpf.formatar(d.cpf) else d.cpf), "MATRÍCULA:" to d.matricula),
            Triple("FUNÇÃO:" to d.funcao.uppercase(), "SETOR:" to d.setor.uppercase(), "DATA DE ADMISSÃO:" to d.admissao)
        ).forEach { (c1, c2, c3) ->
            val bottom = y - hId
            pg.rect(X0, bottom, X1 - X0, hId)
            pg.vline(xa, bottom, y)
            pg.vline(xb, bottom, y)
            celula(X0, xa, bottom, c1.first, c1.second)
            celula(xa, xb, bottom, c2.first, c2.second)
            celula(xb, X1, bottom, c3.first, c3.second)
            y = bottom
        }
        y -= 12

        // Declaração NR-06 + assinatura
        val fsDecl = 9.0
        val lhDecl = 11.5
        PdfTexto.wrap(declaracao(d.empresa.ifBlank { "HTMS ENERGIA LTDA" }), X1 - X0, fsDecl, false).forEach {
            pg.text(it, X0, y - fsDecl, fsDecl, false)
            y -= lhDecl
        }
        y -= 16
        val rotAss = "Assinatura do funcionário:"
        pg.text(rotAss, X0, y - 9, 9.5, true)
        val xAss = X0 + PdfTexto.largura(rotAss, 9.5, true) + 8
        pg.hline(xAss, xAss + 300, y - 10)
        y -= 26

        // Tabela de entrega
        val totalCol = COLUNAS.sum()
        val xs = DoubleArray(COLUNAS.size + 1)
        xs[0] = X0
        for (i in COLUNAS.indices) xs[i + 1] = xs[i] + (X1 - X0) * COLUNAS[i] / totalCol
        val hHead = 20.0
        pg.fill(X0, y - hHead, X1 - X0, hHead, 0xD9D9D9)
        pg.rect(X0, y - hHead, X1 - X0, hHead)
        for (i in 1 until COLUNAS.size) pg.vline(xs[i], y - hHead, y)
        CABECALHO.forEachIndexed { i, s ->
            val fs = if (PdfTexto.largura(s, 8.0, true) > xs[i + 1] - xs[i] - 4) 6.8 else 8.0
            pg.textCentered(s, xs[i], xs[i + 1], y - hHead / 2 - 3, fs, true)
        }
        y -= hHead

        val nLinhas = maxOf(LINHAS_MINIMAS, d.linhas.size)
        val rowH = min(20.0, (y - BOTTOM) / nLinhas)
        val fs = if (rowH < 14.0) 7.5 else 8.5
        for (r in 0 until nLinhas) {
            val bottom = y - rowH
            pg.rect(X0, bottom, X1 - X0, rowH)
            for (i in 1 until COLUNAS.size) pg.vline(xs[i], bottom, y)
            d.linhas.getOrNull(r)?.let { l ->
                val base = bottom + rowH / 2 - fs * 0.35
                pg.textCentered(d.dataEntrega, xs[0], xs[1], base, fs, false)
                pg.textCentered(PdfTexto.truncar(l.quantidade, xs[2] - xs[1] - 4, fs, false), xs[1], xs[2], base, fs, false)
                pg.textCentered(PdfTexto.truncar(l.ca.ifBlank { "N/A" }, xs[3] - xs[2] - 4, fs, false), xs[2], xs[3], base, fs, false)
                pg.text(PdfTexto.truncar(l.descricao, xs[4] - xs[3] - PAD * 2, fs, false), xs[3] + PAD, base, fs, false)
            }
            y = bottom
        }

        return pg.finish("Ficha de EPI")
    }
}
